import {
  commonParentDomain,
  getBranchPref,
  normaliseHost,
  revealApp,
  setBranchPref,
  useBranchStore,
} from '@agos/customer-core'
import { T, tt, useModalFocus } from '@agos/shared'
import { Check, MapPin, Store, X } from 'lucide-react'
import { useEffect, useRef } from 'react'

import {
  branchUrl,
  currentBranch,
  domainsOf,
  isLocalHost,
  mayPinPreference,
  switchNote,
  welcomeNote,
  type Branch,
  type BranchRegistry,
} from './branches'

/**
 * The choice screen, in legacy's two modes.
 *
 * **Welcome** — a first-time visitor, full screen, no way out. The brand's mark, *Vyberte si pobočku*,
 * and a card per branch. There is nothing to dismiss to: every screen behind it assumes a branch.
 *
 * **Change** — somebody who already chose, opened from the header chip or the footer link. A dismissable
 * modal over the page they were on. Dismissing pins the branch they are currently on rather than leaving
 * the preference empty, so closing the dialog is not a decision to be asked again on the next page.
 *
 * The apex domain — fewest labels — is marked *Hlavná*. It is a real distinction to a customer: it is the
 * address on the flyers.
 */
export function BranchPicker({
  registry,
  mode,
  onChosen,
}: {
  registry: BranchRegistry
  mode: 'welcome' | 'change'
  onChosen: () => void
}) {
  const closePicker = useBranchStore((state) => state.closePicker)
  const currentHost = normaliseHost(typeof location === 'undefined' ? '' : location.hostname)
  /**
   * The domain the preference cookie is scoped to.
   *
   * In production it is the brand's common parent — `pizzamajstro.sk` — so a choice made on one branch
   * is remembered on the next. **On a development origin it must be `null`**: a page on `localhost`
   * cannot set a cookie for `pizzamajstro.sk` and the browser drops it silently, which left the switch
   * reloading in place and resolving to the same tenant as before. Host-only is both accepted and
   * correct there, since localhost is the only origin.
   */
  const parent = isLocalHost(currentHost) ? null : commonParentDomain(domainsOf(registry))

  function dismiss() {
    if (mode === 'welcome') return
    // Closing is not "ask me again". Whoever is here is on a branch; pin it.
    //
    // **Except on a soft-launched one** - §10A.131. The cookie is scoped to the brand's common
    // parent, so pinning a hidden branch would `redirect` this visitor back to it from the **main**
    // site later on. `mayPinPreference` is the whole of the soft launch on this side.
    if (getBranchPref() === null && mayPinPreference(registry)) setBranchPref(currentHost, parent)
    closePicker()
  }

  useEffect(() => {
    // The choice screen has mounted, so the boot splash has done its job.
    revealApp()
    /* FB-967, §9.640 - **the lock is conditional and the reveal is not.**
     *
     * This component returns `null` at the `branches.length < 2` guard below, and a guard cannot stop an
     * effect declared above it: React runs this one and then renders nothing, so the page was left fully
     * visible, fully interactive and **unscrollable**, with no overlay for the visitor to dismiss and the
     * cleanup waiting on an unmount that never comes.
     *
     * Measured on the owner's own registry: `/central/branches` answers **0 branches** for a franchise with
     * no `franchise_domains` row, and a cookie naming a sibling domain that registry has never heard of
     * returns `ask` whatever the count - so the chooser mounts to draw nothing.
     *
     * **Hoisting the guard over the hooks is not the fix**: four hooks follow it, so a conditional return
     * above them is a conditional hook and `react-hooks/rules-of-hooks` refuses it. The lock moves inside
     * instead. `revealApp()` stays above the guard deliberately - the splash must come down whether or not
     * there is a choice to offer, or this path hides the page until `index.html`'s 2 500 ms timeout.
     */
    if (registry.branches.length < 2) return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    /* §9.496 - **the container focus stays and the Escape listener goes.** §9.480's regression is two
       mechanisms for one behaviour, so the `keydown` listener is deleted and `useModalFocus` answers Escape
       element-level. This `focus()` is not a duplicate of anything: the hook's opening focus is
       `input ?? [data-dialog-cancel]` and the welcome overlay has **neither**, so without this the first-run
       screen opens with focus on `<body>`. */
    const focus = window.setTimeout(() => (welcomeRef.current ?? changeRef.current)?.focus(), 0)
    return () => {
      document.body.style.overflow = previousOverflow
      window.clearTimeout(focus)
    }
    // `dismiss` closes over `mode`, which is the only thing about it that changes. The branch count is
    // listed because the lock now reads it, so a registry that arrives late releases the page - and with it
    // listed the deps are complete, so the `exhaustive-deps` disable this line used to carry is gone. Lint
    // reported it as unused, which is the rule confirming the dependency rather than a formality.
  }, [mode, registry.branches.length])

  /* §9.496, `TODO.md` 3f - **a ref and a hook each, not one shared pair.** The two overlays are mutually
     exclusive on `mode`, so one hook on one ref would have worked - and it would have made this file the only
     one where the census's *one hook per overlay* equality needed an exception. An exemption avoided by a better
     shape beats an exemption with a good reason (§3.133), and per-overlay `open` flags and closes are clearer
     anyway.

     **`null` for the welcome chooser is the honest close.** `dismiss()` opens with
     `if (mode === 'welcome') return` because whoever is on that screen has no branch yet, so there is nothing to
     dismiss to - and §9.480's third parameter exists to say *not dismissible right now*. This is the first place
     that value is the answer rather than a busy flag. */
  const welcomeRef = useRef<HTMLDivElement>(null)
  const { onKeyDown: welcomeKeyDown } = useModalFocus(mode === 'welcome', welcomeRef, null)
  const changeRef = useRef<HTMLDivElement>(null)
  const { onKeyDown: changeKeyDown } = useModalFocus(mode !== 'welcome', changeRef, dismiss)

  if (registry.branches.length < 2) return null

  const sorted = [...registry.branches].sort(
    (left, right) =>
      left.primaryDomain.split('.').length - right.primaryDomain.split('.').length ||
      left.primaryDomain.length - right.primaryDomain.length ||
      left.id - right.id,
  )
  const primaryId = sorted[0]?.id

  function choose(branch: Branch) {
    const target = normaliseHost(branch.primaryDomain)
    setBranchPref(target, parent)
    if (target === currentHost) {
      onChosen()
      closePicker()
      return
    }
    location.replace(branchUrl(branch, currentHost))
  }

  const cards = (
    <div className="space-y-2.5">
      {sorted.map((branch) => {
        const isCurrent = normaliseHost(branch.primaryDomain) === currentHost
        return (
          <button
            key={branch.id}
            type="button"
            data-branch={branch.id}
            onClick={() => choose(branch)}
            aria-label={`${tt('Objednávať z')} ${branch.name}`}
            className={`w-full flex items-center justify-between gap-3 px-4 py-4 rounded-xl border text-left transition-all hover:shadow-md ${
              isCurrent
                ? 'border-primary bg-primary/5'
                : 'border-border hover:border-primary/60 hover:bg-secondary'
            }`}
          >
            <span className="flex items-center gap-3 min-w-0">
              <span className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <MapPin className="w-5 h-5 text-primary" />
              </span>
              <span className="min-w-0">
                <span className="block font-semibold text-foreground truncate">{branch.name}</span>
                <span className="block text-xs text-muted-foreground truncate">{branch.primaryDomain}</span>
              </span>
            </span>
            <span className="flex items-center gap-2 shrink-0">
              {branch.id === primaryId && (
                <span className="text-[10px] font-bold uppercase tracking-wider text-primary bg-primary/10 px-2 py-1 rounded-full">
                  <T>Hlavná</T>
                </span>
              )}
              {isCurrent && <Check className="w-5 h-5 text-primary" />}
            </span>
          </button>
        )
      })}
    </div>
  )

  if (mode === 'welcome') {
    return (
      <div
        className="fixed inset-0 z-[100] flex flex-col items-center justify-center p-5 bg-background overflow-y-auto"
        data-branch-picker="welcome"
      >
        <div
          ref={welcomeRef}
          onKeyDown={welcomeKeyDown}
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
          aria-label={tt('Vyberte pobočku')}
          className="w-full max-w-md outline-none py-6"
        >
          <div className="text-center mb-7">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary/10 mb-4">
              <Store className="w-7 h-7 text-primary" />
            </div>
            <h1 className="text-2xl font-bold text-foreground" data-branch-welcome>
              <T>Vyberte si pobočku</T>
            </h1>
            {/* §9.73 - the brand group, where the registry names one. `welcomeNote` was written for
              * this line and never called, so a customer arriving at a multi-brand host was asked to
              * choose a branch without being told whose. */}
            <p className="text-sm text-muted-foreground mt-1.5" data-branch-welcome-note>
              {welcomeNote(registry)}
            </p>
          </div>
          {cards}
        </div>
      </div>
    )
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      data-branch-picker="change"
      onClick={dismiss}
      // The backdrop is a dismiss target, not a control: the dialog's own close button and Escape are
      // what a keyboard uses, so this needs no role of its own.
      role="presentation"
    >
      <div
        ref={changeRef}
        onKeyDown={changeKeyDown}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={tt('Zmeniť pobočku')}
        onClick={(event) => event.stopPropagation()}
        className="w-full max-w-md rounded-2xl bg-background shadow-2xl border border-border overflow-hidden outline-none"
      >
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
            <MapPin className="w-5 h-5 text-primary" /> <T>Zmeniť pobočku</T>
          </h2>
          <button
            type="button"
            onClick={dismiss}
            aria-label={tt('Zavrieť')}
            data-dialog-cancel
            className="p-1.5 rounded-full hover:bg-secondary text-muted-foreground"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        {/* §9.35 - `switchNote` was written with this dialog and never called, so the one thing a
          * customer needs to know before switching was never said: `use-cart.ts` keeps the basket in
          * localStorage, which does not cross origins, and a branch is a separate domain. Somebody
          * with a full basket switched and lost it silently. */}
        <p className="px-5 pt-3 text-xs text-muted-foreground" data-branch-switch-note>
          {switchNote(currentBranch(registry))}
        </p>
        <div className="p-4">{cards}</div>
      </div>
    </div>
  )
}
